import type { SuperCopyOperationStatus, SuperCopyReviewDraft } from '@oysterjs/smartfill-contracts';
import { useEffect, useRef, useState } from 'react';
import { SmartFillBridgeDisconnectedError, SmartFillBridgeRequestError } from '../bridge/client';
import { useSidebarSession } from '../bridge/session';
import { CheckIcon, CopyIcon } from '../components/Icons';
import { InlineError, SidebarState } from '../components/SidebarState';
import { VisibilitySelect } from '../components/VisibilitySelect';
import { useSidebarHistory } from '../sidebar/history';
import { BUCKETS_PATH, bucketPath } from '../sidebar/routes';
import { useBridgeAction, useKeyedBridgeAction } from '../sidebar/data';
import { ClientReview } from '../components/SuperCopyReview';
import { toSavePayload, updateClient, validateDraft } from '../sidebar/superCopyDraft';
import { bucketInitial, pluralize } from '../sidebar/presentation';

const SUPER_COPY_SAVE_REQUEST_TIMEOUT_MS = 120_000;

// How long each progress phase may run before the panel offers a way out.
// Each sits above the content script's own bound for that step (an 11 s
// capture, a 30 s extraction), so a stall here means the run is truly stuck.
const STALL_BUDGET_MS = {
  checking: 15_000,
  capturing: 20_000,
  extracting: 45_000,
  restoring: 10_000
} as const;

export function SuperCopyView() {
  const { client, snapshot, snapshotRevision, refresh, selectActiveBucket, applySuperCopy } =
    useSidebarSession();
  const history = useSidebarHistory();
  const superCopy = snapshot.superCopy;
  const [localDraft, setDraft] = useState<SuperCopyReviewDraft | null>(superCopy.draft);
  // An authoritative null/new draft wins during render, before effects run;
  // local edits only belong to the current account's same captured draft.
  const draft = superCopy.draft?.draftId === localDraft?.draftId ? localDraft : superCopy.draft;
  const action = useBridgeAction();
  const activation = useKeyedBridgeAction<string>();
  const initializedDraftId = useRef<string | null>(superCopy.draft?.draftId ?? null);

  useEffect(() => {
    if (!superCopy.draft) {
      initializedDraftId.current = null;
      setDraft(null);
      return;
    }
    if (initializedDraftId.current === superCopy.draft.draftId) return;
    initializedDraftId.current = superCopy.draft.draftId;
    setDraft(superCopy.draft);
  }, [superCopy.draft]);

  // A save whose response was lost may still have completed; once the pushed
  // state says so, the stale request error has nothing left to report.
  const clearRequestError = action.clearError;
  useEffect(() => {
    if (superCopy.phase === 'saved') clearRequestError();
  }, [superCopy.phase, clearRequestError]);

  const requestError = action.error ?? activation.error;

  const start = () =>
    action.run('Page capture could not start.', async () => {
      const sinceRevision = snapshotRevision('superCopy');
      applySuperCopy(await client.request('superCopy.start', {}), { sinceRevision });
    });

  const cancel = () =>
    action.run('Page capture could not be cancelled.', async () => {
      const sinceRevision = snapshotRevision('superCopy');
      applySuperCopy(await client.request('superCopy.cancel', {}), { sinceRevision });
      history.go(BUCKETS_PATH);
    });

  // Cancelling first supersedes whatever the stuck run still publishes, so the
  // relaunch cannot be repainted by a late event from its predecessor.
  const restart = () =>
    action.run('Page capture could not restart.', async () => {
      await client.request('superCopy.cancel', {});
      const sinceRevision = snapshotRevision('superCopy');
      applySuperCopy(await client.request('superCopy.start', {}), { sinceRevision });
    });

  const save = () => {
    if (!draft) return;
    void action.run('The captured data could not be saved.', async () => {
      const sinceRevision = snapshotRevision('superCopy');
      let result;
      try {
        result = await client.request('superCopy.save', toSavePayload(draft), {
          timeoutMs: SUPER_COPY_SAVE_REQUEST_TIMEOUT_MS
        });
      } catch (error) {
        // The content script finishes the save whether or not this request
        // outlives a worker restart or its deadline, so re-read the extension's
        // state instead of trusting the last event the panel happened to see.
        if (isLostResponse(error)) refresh();
        throw error;
      }
      if (applySuperCopy(result, { sinceRevision }) && result.draft) {
        initializedDraftId.current = result.draft.draftId;
        setDraft(result.draft);
      }
    });
  };

  const activate = async (operation: SuperCopyOperationStatus) => {
    const bucketId = operation.bucketId;
    if (!bucketId) return;
    await activation.run(bucketId, 'Could not activate this bucket.', async () => {
      await selectActiveBucket(bucketId);
      history.go(bucketPath(bucketId));
    });
  };

  if (superCopy.phase === 'idle') {
    return (
      <SidebarState
        compact
        title="Create a bucket from this page"
        description="Start a new copy from the page open in this tab."
        action={
          <button
            type="button"
            className="button button--primary button--pill"
            disabled={action.busy}
            onClick={() => void start()}
          >
            {action.busy ? 'Starting…' : 'Create bucket from page'}
          </button>
        }
      />
    );
  }
  if (
    superCopy.phase === 'checking' ||
    superCopy.phase === 'capturing' ||
    superCopy.phase === 'extracting'
  ) {
    return (
      <SuperCopyProgress
        {...progressCopy(superCopy.phase)}
        stallAfterMs={STALL_BUDGET_MS[superCopy.phase]}
        busy={action.busy}
        onCancel={() => void cancel()}
        onRestart={() => void restart()}
      />
    );
  }
  if (superCopy.phase === 'error' && !draft) {
    return (
      <section className="super-copy-view">
        <SidebarState
          compact
          kind="error"
          title="Page capture could not finish"
          description={
            requestError?.message ??
            superCopy.error?.message ??
            'Cara could not prepare this page for review.'
          }
          action={
            <div className="state-actions">
              <button
                type="button"
                className="button button--primary button--pill"
                disabled={action.busy}
                onClick={() => void start()}
              >
                Try again
              </button>
              <button
                type="button"
                className="button button--secondary button--pill"
                onClick={() => void cancel()}
              >
                Cancel
              </button>
            </div>
          }
        />
      </section>
    );
  }
  const complete =
    superCopy.operations.length > 0 &&
    superCopy.operations.every((operation) => operation.state === 'COMPLETE');
  if (superCopy.phase === 'saved' && complete && !draft) {
    return (
      <section className="super-copy-view" aria-labelledby="super-copy-heading">
        <SuperCopyHeader />
        {requestError && <InlineError message={requestError.message} />}
        <SuperCopyComplete
          operations={superCopy.operations}
          activating={activation.busy}
          onActivate={activate}
          onBack={() => history.go(BUCKETS_PATH)}
        />
      </section>
    );
  }
  if (!draft) {
    // Unreachable for any snapshot the contract admits, but a state the panel
    // cannot render must still offer the same exits as a stalled run.
    return (
      <SuperCopyProgress
        title="Restoring page capture"
        description="Loading the private review for this page."
        stallAfterMs={STALL_BUDGET_MS.restoring}
        busy={action.busy}
        onCancel={() => void cancel()}
        onRestart={() => void restart()}
      />
    );
  }

  const validation = validateDraft(draft);
  const startedSaving = superCopy.operations.some((operation) => operation.state !== 'READY');
  const terminalFailure = superCopy.operations.some(
    (operation) => operation.error !== null && !operation.error.retryable
  );
  const retryableFailure = superCopy.operations.some(
    (operation) =>
      operation.state !== 'COMPLETE' && (operation.error === null || operation.error.retryable)
  );
  const saving = superCopy.phase === 'saving';

  return (
    <section className="super-copy-view" aria-labelledby="super-copy-heading">
      <SuperCopyHeader />

      <div className="super-copy-source">
        <span>Copied from</span>
        <strong>{draft.source.pageTitle || draft.source.boundaryLabel || 'Current page'}</strong>
        <small>
          {draft.source.boundaryKind === 'WHOLE_PAGE' ? 'Whole page' : draft.source.boundaryLabel}
          {draft.source.truncated ? ' · Long page shortened' : ''}
        </small>
      </div>

      <div className="super-copy-privacy">
        <label htmlFor="super-copy-visibility">Who can use these data buckets?</label>
        <VisibilitySelect
          id="super-copy-visibility"
          value={draft.visibility}
          disabled={saving || startedSaving}
          onChange={(visibility) => setDraft({ ...draft, visibility })}
        />
        <p>
          Cara reads a capture of this page to find these fields. The sidebar receives only this
          reviewed list.
        </p>
      </div>

      <div className="super-copy-clients">
        {draft.clients.map((reviewClient, clientIndex) => {
          const operation = superCopy.operations.find(
            (candidate) => candidate.clientId === reviewClient.id
          );
          const locked = Boolean(operation && operation.state !== 'READY');
          return (
            <ClientReview
              key={reviewClient.id}
              client={reviewClient}
              index={clientIndex}
              operation={operation}
              disabled={saving || locked}
              invalid={validation.invalid}
              conflicts={validation.conflicts}
              onChange={(next) => setDraft(updateClient(draft, next))}
              onOpenBucket={(failed) => void activate(failed)}
            />
          );
        })}
      </div>

      {(requestError || superCopy.error) && (
        <InlineError message={requestError?.message ?? superCopy.error?.message ?? ''} />
      )}
      {validation.messages.length > 0 && !complete && (
        <div className="super-copy-validation" role="status">
          <strong>Review before saving</strong>
          <span>{validation.messages[0]}</span>
        </div>
      )}

      {complete ? (
        <SuperCopyComplete
          operations={superCopy.operations}
          activating={activation.busy}
          onActivate={activate}
          onBack={() => history.go(BUCKETS_PATH)}
        />
      ) : (
        <div className="super-copy-actions">
          {!startedSaving && (
            <button
              type="button"
              className="button button--secondary button--pill"
              disabled={saving}
              onClick={() => void cancel()}
            >
              Cancel
            </button>
          )}
          {terminalFailure && (
            <button
              type="button"
              className="button button--secondary button--pill"
              disabled={saving}
              onClick={() => void cancel()}
            >
              Discard unfinished copy
            </button>
          )}
          {(!startedSaving || retryableFailure) && (
            <button
              type="button"
              className="button button--primary button--pill"
              aria-busy={saving}
              disabled={saving || validation.messages.length > 0}
              onClick={() => void save()}
            >
              {saving
                ? 'Saving…'
                : startedSaving
                  ? 'Retry unfinished clients'
                  : draft.clients.length === 1
                    ? 'Save data bucket'
                    : `Save ${draft.clients.length} data buckets`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function SuperCopyHeader() {
  return (
    <header className="super-copy-header">
      <span className="super-copy-mark" aria-hidden="true">
        <CopyIcon />
      </span>
      <span>
        <span className="eyebrow">New data buckets</span>
        <h1 id="super-copy-heading">Create bucket from page</h1>
      </span>
    </header>
  );
}

function SuperCopyComplete({
  operations,
  activating,
  onActivate,
  onBack
}: {
  operations: SuperCopyOperationStatus[];
  activating: string | null;
  onActivate: (operation: SuperCopyOperationStatus) => Promise<void>;
  onBack: () => void;
}) {
  return (
    <div className="super-copy-complete">
      <div className="super-copy-complete__summary">
        <span className="super-copy-complete__mark" aria-hidden="true">
          <CheckIcon />
        </span>
        <strong>
          {operations.length} {pluralize(operations.length, 'bucket')} ready
        </strong>
        <p>Choose one to make it your active fill source.</p>
      </div>
      <div className="bucket-list">
        {operations.map((operation) => {
          const name = operation.bucketName ?? 'saved bucket';
          return (
            <article key={operation.operationId} className="bucket-row">
              <button
                type="button"
                className="bucket-row__header"
                aria-label={`Use ${name}`}
                disabled={!operation.bucketId || activating !== null}
                onClick={() => void onActivate(operation)}
              >
                <span className="bucket-avatar">{bucketInitial(name)}</span>
                <span className="bucket-row__copy">
                  <strong>{name}</strong>
                  <span>Created from this page</span>
                </span>
                <span className="use-label">Use</span>
              </button>
            </article>
          );
        })}
      </div>
      <button type="button" className="button button--secondary button--pill" onClick={onBack}>
        Back to data buckets
      </button>
    </div>
  );
}

function SuperCopyProgress({
  title,
  description,
  stallAfterMs,
  busy,
  onCancel,
  onRestart
}: {
  title: string;
  description: string;
  stallAfterMs: number;
  busy: boolean;
  onCancel: () => void;
  onRestart: () => void;
}) {
  const [stalled, setStalled] = useState(false);

  // The budget restarts with every phase: a run that keeps advancing is not
  // stuck, however long the whole pipeline takes.
  useEffect(() => {
    setStalled(false);
    const timer = window.setTimeout(() => setStalled(true), stallAfterMs);
    return () => window.clearTimeout(timer);
  }, [title, stallAfterMs]);

  return (
    <section className="super-copy-view">
      <SidebarState
        compact
        kind="loading"
        title={title}
        description={stalled ? 'This is taking longer than expected.' : description}
        action={
          <div className="state-actions">
            {stalled && (
              <button
                type="button"
                className="button button--primary button--pill"
                disabled={busy}
                onClick={onRestart}
              >
                Try again
              </button>
            )}
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        }
      />
      <div className="super-copy-progress" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </section>
  );
}

function isLostResponse(error: unknown): boolean {
  return (
    error instanceof SmartFillBridgeDisconnectedError ||
    (error instanceof SmartFillBridgeRequestError && error.code === 'TIMEOUT')
  );
}

function progressCopy(phase: 'checking' | 'capturing' | 'extracting'): {
  title: string;
  description: string;
} {
  switch (phase) {
    case 'checking':
      return {
        title: 'Checking this page',
        description: 'Looking for readable client information.'
      };
    case 'capturing':
      return {
        title: 'Capturing this page',
        description: 'Keeping the source inside the extension for your review.'
      };
    case 'extracting':
      return {
        title: 'Building your review',
        description: 'Organizing the copied values into fresh buckets.'
      };
  }
}
